import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Award, TrendingUp, ClipboardCheck, CheckCircle2, XCircle, BookOpen } from "lucide-react";

export default function Grades() {
  const [programs, setPrograms] = useState([]);
  const [selected, setSelected] = useState("");
  const [grades, setGrades] = useState(null);

  useEffect(() => {
    api.get("/enrollments").then((r) => {
      const approved = r.data.filter((e) => e.status === "approved");
      setPrograms(approved);
      if (approved[0]) setSelected(approved[0].program_id);
    }).catch(() => setPrograms([]));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setGrades(null);
    api.get(`/grades?program_id=${selected}`).then((r) => setGrades(r.data)).catch(() => setGrades(false));
  }, [selected]);

  if (programs.length === 0) return (
    <>
      <PageHeader title="درجاتي" subtitle="نتائجك في البرامج العلمية" />
      <EmptyState icon={BookOpen} title="لا توجد برامج مقبولة" desc="سجّل في برنامج وانتظر القبول لعرض درجاتك." />
    </>
  );

  return (
    <>
      <PageHeader title="درجاتي" subtitle="نتائجك محسوبة من الحضور والواجبات"
        action={
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="grades-program" className="w-56 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{programs.map((e) => <SelectItem key={e.program_id} value={e.program_id}>{e.program_name}</SelectItem>)}</SelectContent>
          </Select>
        }
      />
      {grades === null ? <PageLoader /> : grades === false ? <EmptyState icon={Award} title="تعذّر تحميل الدرجات" /> : (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className={`rounded-2xl p-6 text-white soft-shadow ${grades.passed ? "bg-greenx" : "bg-navy"}`}>
            <div className="flex items-center gap-2">{grades.passed ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}<span className="text-sm">النتيجة النهائية</span></div>
            <p className="mt-4 font-heading text-6xl font-extrabold">{grades.final}<span className="text-2xl">%</span></p>
            <p className="mt-2 text-lg font-bold text-gold">{grades.grade_letter}</p>
            <p className="mt-1 text-sm text-white/70">{grades.passed ? "مؤهل للحصول على الشهادة" : "لم تستوفِ شروط النجاح بعد"}</p>
          </div>

          <div className="rounded-2xl border border-slate-100 bg-white p-6 soft-shadow">
            <div className="mb-3 flex items-center gap-2 text-navy"><TrendingUp className="h-5 w-5" /><span className="font-heading font-bold">الحضور (40%)</span></div>
            <p className="font-heading text-3xl font-extrabold text-greenx">{grades.attendance.percent}%</p>
            <p className="mt-1 text-sm text-slate-500">{grades.attendance.attended_hours} / {grades.attendance.total_hours} ساعة</p>
            <Progress value={grades.attendance.percent} className="mt-3 h-2.5" />
            <p className="mt-2 text-xs text-slate-400">الحد الأدنى المطلوب: {grades.attendance.min_attendance}%</p>
          </div>

          <div className="rounded-2xl border border-slate-100 bg-white p-6 soft-shadow">
            <div className="mb-3 flex items-center gap-2 text-navy"><ClipboardCheck className="h-5 w-5" /><span className="font-heading font-bold">الواجبات (60%)</span></div>
            <p className="font-heading text-3xl font-extrabold text-navy">{grades.assignments_avg}%</p>
            <Progress value={grades.assignments_avg} className="mt-3 h-2.5" />
          </div>

          <div className="lg:col-span-3 rounded-2xl border border-slate-100 bg-white p-6 soft-shadow">
            <h3 className="mb-4 font-heading text-lg font-bold text-navy">تفاصيل الواجبات</h3>
            {grades.assignments.length === 0 ? <p className="py-4 text-center text-sm text-slate-400">لا توجد واجبات</p> : (
              <div className="space-y-2">
                {grades.assignments.map((a, i) => (
                  <div key={i} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3">
                    <span className="text-sm font-semibold text-navy">{a.title}</span>
                    <span className={`text-sm font-bold ${a.grade === null ? "text-slate-300" : "text-greenx"}`}>{a.grade === null ? "لم يُقيّم" : `${a.grade} / ${a.max_grade}`}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
